import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme, withStyles } from '@mui/styles';
import Content from 'studio/components/Content/Content';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import PublishIcon from '@mui/icons-material/PublishRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import { useMessage } from 'studio/components/Message';
import styles from './TenantAccess.styles';
import Typography from '@mui/material/Typography';
import { trackEvent } from 'utils/eventTracking';
import Progress from 'studio/components/Progress';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import { TenantsIcon } from 'components/Icons';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import { pluralize as plrl } from 'studio/helpers';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Chip from 'studio/components/Chip/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import WarningIcon from '@mui/icons-material/WarningRounded';
import Tooltip from '@mui/material/Tooltip';
import _ from 'lodash';
import ErrorIcon from '@mui/icons-material/ErrorRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import classNames from 'classnames';
import Link from 'studio/components/Link';
import { veezooUrls } from 'config/constants';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PageTitle from 'studio/components/PageTitle';

// Tooltip with fixed width
const VariableAssignmentTooltip = withStyles({ tooltip: { width: '500px' } })(Tooltip);

const TenantAccess = ({ classes, knowledgeGraph }) => {
  const theme = useTheme();

  useEffect(() => {
    trackEvent('Studio Tenant Access Opened');
  }, []);

  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();
  const { showConfirmationModal } = useConfirmationModal();

  // the tenants
  const [tenants, setTenants] = useState(null);
  // the tenant knowledge graphs for the current knowledge graph
  const [tenantKgs, setTenantKgs] = useState(null);
  // the IDs of the tenant knowledge graphs that are selected in the data grid
  const [selectedTenantKgIds, setSelectedTenantKgIds] = useState([]);
  // specifies whether the tenant knowledge graph dialog is open
  const [showCreationDialog, setShowCreationDialog] = useState(false);
  // the tenants that are selected in the creation dialog to create tenant knowledge graphs for
  const [selectedTenants, setSelectedTenants] = useState([]);

  // whether the variable assignment dialog is open
  const [showVariableAssignmentDialog, setShowVariableAssignmentDialog] = useState(false);
  // the variable assignment that is currently being edited (consisting of declaredVariables and assignment)
  const [editingVariableAssignment, setEditingVariableAssignment] = useState(null);
  // the KG to edit the variable assignment of
  const [editingVariableAssignmentOfKg, setEditingVariableAssignmentOfKg] = useState(null);
  // the variable assignments for the main and the tenant knowledge graphs, missing if not yet being loaded, undefined if currently being loaded, null if error
  const [kgVariableAssignments, setKgVariableAssignments] = useState({});

  // the version timestamps for the main and the tenant knowledge graphs, missing if not yet being loaded, undefined if currently being loaded, null if error or not available
  const [kgVersionTimestamps, setKgVersionTimestamps] = useState({});

  // the tenants which don't have a tenant knowledge graph yet
  const remainingTenants = tenants?.filter(tenant => !tenantKgs?.some(kg => kg.data.tenantId === tenant.data.id));

  // The tenant KGs and the main KG (memoized so that its identity doesn't change on every render, allowing its use in useEffect)
  const allKgs = useMemo(() => [...(tenantKgs || []), knowledgeGraph], [tenantKgs, knowledgeGraph]);

  // Render a variable assignment by showing the values of the variables, possible warnings / errors and an edit button
  const renderVariableAssignment = (kg, variableAssignment) => {
    const assignment = variableAssignment?.assignment || {};
    // List of variable names and an HTML element that shows the name and value of the variable
    const structuredAssignments = Object.entries(assignment)?.map(([variable, value]) => {
      return [
        variable,
        <span key={variable}>
          {variable}: {value}
        </span>
      ];
    });
    // Sort the variable assignments by variable name
    structuredAssignments.sort(([variableA], [variableB]) => variableA.localeCompare(variableB));

    // Missing variables are variables that are declared but not assigned, sorted alphabetically
    const missingVariables = variableAssignment?.declaredVariables?.filter(v => !(v in assignment)) || [];
    missingVariables.sort();

    // List of variables and values to show in the tooltip, as well as missing variable names
    const tooltipContent = (
      <>
        <ul style={{ paddingLeft: '1rem' }}>
          {structuredAssignments?.map(([variable, element]) => (
            <li key={variable}>{element}</li>
          ))}
        </ul>

        {missingVariables.length > 0 && (
          <>
            Missing Variable Values:
            <ul style={{ paddingLeft: '1rem' }}>
              {missingVariables.map(variable => (
                <li key={variable}>{variable}</li>
              ))}
            </ul>
          </>
        )}
      </>
    );

    return variableAssignment === undefined ? (
      // Show loading indicator until we have loaded the variable assignment
      <CircularProgress size="20px" />
    ) : (
      // Indicate if the variable assignment is missing
      <>
        {variableAssignment === null ? (
          // There was an error loading the variable assignment
          <ErrorIcon style={{ color: theme.palette.error.main }} />
        ) : (
          // make this the anchor element for the edit icon
          <span
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {/* Cell is rendered as "var1: value1, var2: value2, ..." */}
            <div
              style={{
                // Fade text out towards the edit icon
                // Note: browser support is not perfect for this, but modern desktop browsers should all support it and most mobile browsers too,
                //  also it's not a big deal if it doesn't work.
                // See https://caniuse.com/mdn-css_properties_mask-image
                WebkitMaskImage: 'linear-gradient(to left, transparent 0, transparent 28px, black 40px, black)',
                // Make sure the text takes up the whole width of the cell
                width: '100%',
                // Make sure the element is as high as the cell, so that the tooltip is displayed even if there's no text
                height: '100%',

                // Vertically center content
                display: 'flex',
                alignItems: 'center',
                // Space of 10px between the text and the warning icon
                gap: '10px'
              }}
            >
              {/* Show warning if a variable is declared but not assigned, always at the rigtht of the cell for consistency (left of edit icon) */}
              {missingVariables.length > 0 && (
                <Tooltip title="A declared variable has not been assigned">
                  <WarningIcon style={{ color: theme.palette.warning.main }} data-test="warning-icon" />
                </Tooltip>
              )}

              {/* Show the whole text on hover (even if it's not cut off) */}
              <VariableAssignmentTooltip title={tooltipContent}>
                <span>
                  {structuredAssignments?.map(([variable, element], idx) => (
                    <span key={variable}>
                      {element}
                      {idx < structuredAssignments.length - 1 && ', '}
                    </span>
                  ))}
                </span>
              </VariableAssignmentTooltip>
            </div>

            {/* Edit icon to click to edit the variable assignment */}
            <EditIcon
              onClick={event => {
                setShowVariableAssignmentDialog(true);
                setEditingVariableAssignmentOfKg(kg);
                setEditingVariableAssignment(variableAssignment);
                event.stopPropagation();
              }}
              // Edit icon is always at the right of the cell for consistency
              style={{ cursor: 'pointer', position: 'absolute', right: '10px' }}
              data-test="edit-icon"
            />
          </span>
        )}
      </>
    );
  };

  const renderVersionTimestamp = ({ row: { tenantId }, value: timestamp }) => {
    if (timestamp === undefined) {
      return (
        // Show loading indicator until we have loaded the version timestamp
        <CircularProgress size="20px" />
      );
    }

    // Timestamp is in microseconds since unix epoch
    const localString = timestamp ? new Date(timestamp / 1000).toLocaleString() : '—';

    const commonIconProps = {
      display: tenantId ? 'initial' : 'none',
      marginRight: '10px'
    };

    const icon =
      timestamp === kgVersionTimestamps[knowledgeGraph?.data?.id] ? (
        <Tooltip title="This tenant's knowledge graph is up to date with the main knowledge graph">
          <CheckCircle style={{ ...commonIconProps, color: theme.palette.success.main }} data-test="success-icon" />
        </Tooltip>
      ) : (
        <Tooltip title="This tenant's knowledge graph is out of date with the main knowledge graph">
          <WarningIcon style={{ ...commonIconProps, color: theme.palette.warning.main }} data-test="warning-icon" />
        </Tooltip>
      );

    return (
      <>
        {icon}
        {localString}
      </>
    );
  };

  // the columns for the data grid
  const columns = [
    { field: 'tenantId', headerName: 'Tenant ID' },
    { field: 'tenantName', headerName: 'Tenant Name' },
    { field: 'id', headerName: 'Knowledge Graph ID', flex: 2 },
    {
      field: 'assignment',
      headerName: 'Variables',
      flex: 2,
      renderCell: ({ value: [tenantKg, tenantKgAssignment] }) => renderVariableAssignment(tenantKg, tenantKgAssignment)
    },
    { field: 'versionTimestamp', headerName: 'Version', renderCell: renderVersionTimestamp }
  ];

  // rows corresponding to tenant KGs
  const tenantKgRows = tenantKgs?.map(tenantKg => {
    const tenant = tenants?.find(tenant => tenant.data.id === tenantKg.data.tenantId);
    return {
      tenantId: tenant?.data.externalId,
      tenantName: tenant?.data.name,
      id: tenantKg.data.id,
      // "assignment" has information on the tenant KG and its variable assignment (not directly rendered in the column though)
      // The variable assignment will be undefined if it is not yet loaded (or not started loading), and null if it does not exist
      assignment: [tenantKg, kgVariableAssignments[tenantKg.data.id]],
      versionTimestamp: kgVersionTimestamps[tenantKg.data.id]
    };
  });

  // the rows for the data grid
  const tableRows = [
    // First row is the main knowledge graph
    {
      tenantId: undefined,
      tenantName: undefined,
      id: knowledgeGraph?.data?.id,
      assignment: [knowledgeGraph, kgVariableAssignments[knowledgeGraph?.data?.id]],
      versionTimestamp: kgVersionTimestamps[knowledgeGraph?.data?.id]
    },
    ...(tenantKgRows || [])
  ];

  const getTenants = async () => {
    const response = await withLoader(services.getTenants());
    if (response.success) {
      const tenants = response.data.data;
      setTenants(tenants);
    } else {
      const message = response?.response?.data?.data?.message || 'There was an error getting the tenants';
      errorMessage(message);
    }
  };

  const getTenantKnowledgeGraphs = async knowledgeGraph => {
    const response = await withLoader(services.getTenantKnowledgeGraphs(knowledgeGraph.data.id));
    if (response.success) {
      const tenantKgs = response.data.data;
      setTenantKgs(tenantKgs);
    } else {
      const message =
        response?.response?.data?.data?.message || 'There was an error getting the tenant knowledge graphs';
      errorMessage(message);
    }
  };

  const createTenantKnowledgeGraph = async tenant => {
    const { id: tenantId, name: tenantName } = tenant.data;
    const response = await withLoader(services.createTenantKnowledgeGraph(knowledgeGraph.data.id, tenantId));
    if (response.success) {
      trackEvent('Tenant Knowledge Graph Created');
      successMessage(`The knowledge graph has successfully been created for tenant '${tenantName}'`);
    } else {
      const message =
        response.response?.data?.data?.message ||
        `There was an error creating the knowledge graph for tenant ${tenantName}`;
      errorMessage(message);
    }
  };

  const updateTenantKnowledgeGraph = async (tenantKg, tenant) => {
    const { id: tenantId, name: tenantName } = tenant.data;
    const response = await withLoader(services.updateTenantKnowledgeGraph(knowledgeGraph.data.id, tenantId));

    // Reset variable assignment state for this KG to reload the variables
    setKgVariableAssignments(prev => _.omit(prev, tenantKg.data.id));
    setKgVersionTimestamps(prev => _.omit(prev, tenantKg.data.id));

    if (response.success) {
      trackEvent('Tenant Knowledge Graph Updated');
      successMessage(`The knowledge graph for tenant '${tenantName}' has successfully been updated`);
    } else {
      const message =
        response.response?.data?.data?.message ||
        `There was an error updating the knowledge graph for tenant '${tenantName}'`;
      errorMessage(message);
    }
  };

  const saveVariableAssignment = async (kg, { declaredVariables, assignment }) => {
    // Empty string is considered as missing value, it's impossible to assign the empty string to a variable
    const nonEmptyAssignment = _.pickBy(assignment, value => value !== '');

    const save = async () => {
      const response = await withLoader(
        services.setVariableAssignment(kg.data.id, {
          assignment: nonEmptyAssignment
        })
      );

      // Reset variable assignment state for this KG to reload the variables
      setKgVariableAssignments(prev => _.omit(prev, kg.data.id));
      setKgVersionTimestamps(prev => _.omit(prev, kg.data.id));
      // Close modal
      setShowVariableAssignmentDialog(false);

      if (response.success) {
        successMessage(`The variables have successfully been saved`);
      } else {
        const message = response.response?.data?.data?.message || `There was an error saving the variables`;
        errorMessage(message);
      }
    };

    const missingVariables = _.difference(declaredVariables, Object.keys(nonEmptyAssignment));

    // Show confirmation modal with warning if there are missing variables
    if (missingVariables.length > 0) {
      showConfirmationModal(() => ({
        title: 'Save Variable Assignment',
        content: (
          <>
            <p>Do you really want to save the variable assignment?</p>
            The following variable values are missing:
            <ul>
              {missingVariables.map(variable => (
                <li key={variable}>{variable}</li>
              ))}
            </ul>
          </>
        ),
        onConfirm: save,
        maxWidth: 'xs'
      }));
    } else {
      await save();
    }
  };

  const deleteTenantKnowledgeGraph = async (tenantKg, tenant) => {
    const { name: tenantName } = tenant.data;
    const response = await withLoader(services.deleteKnowledgeGraph(tenantKg.data.id));
    if (response.success) {
      trackEvent('Tenant Knowledge Graph Deleted');
      successMessage(`The knowledge graph for tenant '${tenantName}' has successfully been deleted`);
    } else {
      const message =
        response.response?.data?.data?.message ||
        `There was an error deleting the knowledge graph for tenant '${tenantName}'`;
      errorMessage(message);
    }
  };

  useEffect(() => {
    if (knowledgeGraph) {
      getTenants();
      getTenantKnowledgeGraphs(knowledgeGraph);
    }
  }, [knowledgeGraph]);

  // Get the variable assignments for the tenant KGs (where we don't have them yet)
  useEffect(() => {
    // Tenant KGs and main KG
    // Note: check for existence of key, the value is undefined if we are already loading
    const notYetLoadingAssignments = allKgs.filter(kg => !(kg.data.id in kgVariableAssignments));
    if (notYetLoadingAssignments.length > 0) {
      setKgVariableAssignments(prev => ({
        ...prev,
        ...Object.fromEntries(notYetLoadingAssignments.map(kg => [kg.data.id, undefined]))
      }));
    }
    notYetLoadingAssignments?.forEach(async kg => {
      const responses = await Promise.all([
        services.getDeclaredVariables(kg.data.id),
        services.getVariableAssignment(kg.data.id)
      ]);
      if (responses.every(response => response.success || response?.response?.status === 404)) {
        const [declaredVariables, variableAssignment] = responses.map(response => response?.data?.data);
        setKgVariableAssignments(prev => ({
          ...prev,
          [kg.data.id]: { declaredVariables, assignment: variableAssignment?.assignment }
        }));
      } else {
        // There was an error, we still store null to avoid loading it again
        errorMessage(`There was an error getting the variable assignment for knowledge graph ${kg.data.id}`);
        setKgVariableAssignments(prev => ({ ...prev, [kg.data.id]: null }));
      }
    });
  }, [allKgs, kgVariableAssignments, errorMessage]);

  // Get the version timestamp for the KGs (where we don't have them yet)
  useEffect(() => {
    // Note: check for existence of key, the value is undefined if we are already loading
    const notYetLoadingTimestamps = allKgs.filter(kg => !(kg.data.id in kgVersionTimestamps));
    if (notYetLoadingTimestamps.length > 0) {
      setKgVersionTimestamps(prev => ({
        ...prev,
        ...Object.fromEntries(notYetLoadingTimestamps.map(kg => [kg.data.id, undefined]))
      }));
    }
    notYetLoadingTimestamps?.forEach(async kg => {
      const response = await services.getVersionTimestamp(kg.data.id);
      if (response.success) {
        const versionTimestamp = response.data || null;
        setKgVersionTimestamps(prev => ({
          ...prev,
          [kg.data.id]: versionTimestamp
        }));
      } else {
        // There was an error, we still store null to avoid loading it again
        errorMessage(`There was an error getting the version timestamp for knowledge graph ${kg.data.id}`);
        setKgVersionTimestamps(prev => ({ ...prev, [kg.data.id]: null }));
      }
    });
  }, [allKgs, kgVersionTimestamps, errorMessage]);

  const closeCreationDialog = () => {
    setShowCreationDialog(false);
    setSelectedTenants([]);
  };

  // executes the specified requests (functions returning a request) in serial order
  const executeSerially = async requests => {
    await withLoader(
      (async () => {
        for (const request of requests) {
          await request();
        }
      })()
    );
  };

  // creates an action which acts on the selected tenant knowledge graphs in the data grid
  const action = (action, icon, request, props) => {
    const capitalizedAction = action.charAt(0).toUpperCase() + action.slice(1);
    const allRowsSelected = selectedTenantKgIds.length === tenantKgs?.length;
    const subject = allRowsSelected
      ? 'all tenant knowledge graphs'
      : `the selected ${plrl(selectedTenantKgIds, 'tenant knowledge graph')}`;
    return {
      label:
        capitalizedAction +
        (selectedTenantKgIds.length ? (allRowsSelected ? ' all' : ` (${selectedTenantKgIds.length})`) : ''),
      icon,
      tooltip:
        selectedTenantKgIds.length > 0
          ? `${capitalizedAction} ${subject}`
          : `Select one or more tenant knowledge graphs to ${action}`,
      disabled: !selectedTenantKgIds.length,
      onClick: () =>
        showConfirmationModal(() => ({
          title: `${capitalizedAction} ${plrl(selectedTenantKgIds, 'Tenant Knowledge Graph')}`,
          content: `Do you really want to ${action} ${subject}?`,
          onConfirm: async () => {
            await executeSerially(
              selectedTenantKgIds.map(id => {
                const tenantKg = tenantKgs.find(tenantKg => tenantKg.data.id === id);
                const tenant = tenants.find(tenant => tenant.data.id === tenantKg.data.tenantId);
                // function which returns the request to execute and then refreshes the tenant knowledge graphs
                return () => request(tenantKg, tenant).then(() => getTenantKnowledgeGraphs(knowledgeGraph));
              })
            );
            setSelectedTenantKgIds([]);
          }
        })),
      ...props
    };
  };

  const createAction = {
    label: 'Create',
    icon: <AddIcon />,
    tooltip: remainingTenants?.length
      ? 'Create a new tenant knowledge graph based on this knowledge graph'
      : 'Tenant knowledge graphs already exist for all tenants',
    disabled: !remainingTenants?.length,
    onClick: () => setShowCreationDialog(true)
  };
  const updateAction = action('update', <PublishIcon />, updateTenantKnowledgeGraph);
  const deleteAction = action('delete', <DeleteIcon />, deleteTenantKnowledgeGraph, { color: 'secondary' });

  const tenantOfEditedKg = tenants?.find(tenant => tenant.data.id === editingVariableAssignmentOfKg?.data?.tenantId);

  // All declared variables of the KG we are editing
  const declaredVariables = editingVariableAssignment?.declaredVariables || [];
  // The declared variables and the not-declared-but-assigned ones, sorted alphabetically
  const allVariables = _.union(declaredVariables, Object.keys(editingVariableAssignment?.assignment || {}));
  allVariables.sort();

  const variableAssignmentModal = (
    <Dialog
      maxWidth="md"
      className={classes.dialog}
      open={showVariableAssignmentDialog}
      onClose={() => setShowVariableAssignmentDialog(false)}
      data-test="variablesAssignmentDialog"
    >
      <DialogTitle>
        {tenantOfEditedKg ? (
          <>Edit Variables for Tenant {tenantOfEditedKg?.data?.name}</>
        ) : (
          <>Edit Variables for Knowledge Graph</>
        )}
      </DialogTitle>
      <DialogContent>
        {/* Show message if no variable is declared */}
        {allVariables.length === 0 && <>No variables are declared for this knowledge graph.</>}

        {/* Put variables below each other */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {allVariables.map(variable => {
            const value = editingVariableAssignment?.assignment?.[variable] || '';
            return (
              // Put variable name and value side by side on the same height
              <div key={variable} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Variable name */}
                <span
                  style={{
                    width: '350px',
                    textAlign: 'left',
                    marginLeft: '20px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {variable}
                </span>

                {/* Variable value */}
                <TextField
                  size="small"
                  className={classes.inputField}
                  value={value}
                  onChange={event =>
                    setEditingVariableAssignment(prev => ({
                      declaredVariables: prev.declaredVariables,
                      assignment: { ...prev.assignment, [variable]: event.target.value }
                    }))
                  }
                  error={!value} // Mark as error (red) if value is empty
                  style={{ width: '400px' }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveVariableAssignment(editingVariableAssignmentOfKg, editingVariableAssignment);
                    }
                  }}
                  data-test={variable}
                />

                {/* If the variable is not declared, show a warning and a remove button */}
                <Tooltip title="This variable is not declared, the value can not be used.">
                  <WarningIcon
                    style={{
                      color: theme.palette.warning.main,
                      visibility: declaredVariables.includes(variable) ? 'hidden' : 'visible'
                    }}
                  />
                </Tooltip>

                <CloseIcon
                  style={{ cursor: 'pointer', visibility: declaredVariables.includes(variable) ? 'hidden' : 'visible' }}
                  onClick={() =>
                    setEditingVariableAssignment(prev => ({
                      declaredVariables: prev.declaredVariables,
                      assignment: _.omit(prev.assignment, variable)
                    }))
                  }
                />
              </div>
            );
          })}
        </div>
      </DialogContent>

      <DialogActions>
        <Button color="primary" disabled={loading} onClick={_ => setShowVariableAssignmentDialog(false)}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={loading}
          onClick={_ => saveVariableAssignment(editingVariableAssignmentOfKg, editingVariableAssignment)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Use useCallback to avoid a "no row with id found" exception when deleting a tenant KG,
  // see also https://github.com/mui/mui-x/issues/2714#issuecomment-1353261346 for other people encountering the issue,
  // seems like a bug in the datagrid library
  const isRowSelectable = useCallback(row => !!row.row.tenantId, []);

  return (
    <>
      <Content loading={!tenants || !tenantKgs} data-test="tenantAccessContent">
        <div className={classes.container}>
          <PageTitle icon={TenantsIcon}>Tenant Access</PageTitle>
          <Typography variant="body1" gutterBottom>
            Make <i>{knowledgeGraph.data.info.default.name}</i> available for tenants by creating copies of the
            knowledge graph for them. After modifying <i>{knowledgeGraph.data.info.default.name}</i>, update the
            tenant&apos;s knowledge graphs to include the latest changes.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Set values for the variables for each tenant to specify the behavior for them and only allow them to see
            data they should have access to. Not setting necessary variables will result in the knowledge graph not
            being usable for the tenant in many circumstances. The available variables are declared in the knowledge
            graph in VKL.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Refer to the{' '}
            <Link
              href={`${veezooUrls.docs}/embedded/embedded-guide/#4-configure-tenants-in-veezoo`}
              target="_blank"
              rel="noreferrer"
            >
              documentation
            </Link>{' '}
            for more information.
          </Typography>

          {!knowledgeGraph ? (
            <div className={classes.loaderContainer}>
              <Progress />
            </div>
          ) : (
            <DataGrid
              className="tenantKnowledgeGraphsDatagrid"
              pageSize={100}
              rows={tableRows}
              columns={columns.map(column => ({
                disableClickEventBubbling: true,
                sortable: false,
                flex: 1,
                // Render undefined values as a dash
                renderCell: ({ value }) => (value === undefined ? '—' : value),
                ...column
              }))}
              checkboxSelection
              selectionModel={selectedTenantKgIds}
              onSelectionModelChange={newSelectionModel => setSelectedTenantKgIds(newSelectionModel)}
              sortModel={[{ field: 'tenantName', sort: 'asc' }]}
              actions={[createAction, updateAction, deleteAction]}
              isRowSelectable={isRowSelectable}
              disableColumnMenu
              getRowClassName={row => classNames({ [classes.mainKgRow]: !row.row.tenantId })}
            />
          )}
        </div>
      </Content>

      <Dialog
        fullWidth
        maxWidth="sm"
        className={classes.dialog}
        open={showCreationDialog}
        onClose={closeCreationDialog}
        data-test="tenantKnowledgeGraphCreationDialog"
      >
        <DialogTitle>Create Tenant Knowledge Graph</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Create a copy of this knowledge graph for one or multiple tenants to use.
          </Typography>
          <div className={classes.autocompleteContainer}>
            <Autocomplete
              multiple
              options={tenants}
              getOptionLabel={tenant => tenant.data.name}
              getOptionDisabled={tenant =>
                !remainingTenants.some(remainingTenant => remainingTenant.data.id === tenant.data.id)
              }
              filterSelectedOptions
              renderInput={params => <TextField {...params} size="small" label="Tenants" />}
              renderTags={(valueTenants, getTagProps) =>
                valueTenants.map((tenant, index) => (
                  <Chip
                    size="small"
                    icon={<TenantsIcon />}
                    label={tenant.data.name}
                    key={index}
                    {...getTagProps({ index })}
                  />
                ))
              }
              value={selectedTenants}
              onChange={(event, selectedTenants) => setSelectedTenants(selectedTenants)}
              disabled={loading}
            />
            <Button
              variant="outlined"
              color="primary"
              className={classes.selectAllButton}
              disabled={loading}
              onClick={() => setSelectedTenants(remainingTenants)}
            >
              Select all
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" disabled={loading} onClick={closeCreationDialog}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={async () => {
              await executeSerially(
                selectedTenants.map(
                  tenant => () =>
                    createTenantKnowledgeGraph(tenant).then(() => getTenantKnowledgeGraphs(knowledgeGraph))
                )
              );
              closeCreationDialog();
            }}
            disabled={loading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      {variableAssignmentModal}
    </>
  );
};

export default withStyles(styles)(TenantAccess);
